import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Backdrop,
  Alert,
  alpha,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Info as InfoIcon,
  Route as RouteIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import XLSX from "sheetjs-style";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import axiosInstance from "../utlis/axiosInstance";
import moment from "moment";
import { useTheme } from "../utlis/useTheme";
import DeviceSearchSelect, { DeviceOption } from "../components/DeviceSearchSelect";

interface GPSInfo {
  GPS_length: number;
  Satellites: number;
}

interface CourseStatus {
  course: number;
  status: {
    gps_positioned: boolean;
    east_longitude: boolean;
    north_latitude: boolean;
    gps_real_time: boolean;
  };
}

interface ReportData {
  startTime: number;
  endTime: number;
  startOdometer: number;
  endOdometer: number;
  distanceKm: number;
  startLat: number;
  startLong: number;
  endLat: number;
  endLong: number;
  startAddress: string;
  endAddress: string;
  duration?: number;
  batteryLevel?: number | null;
  gpsInfo?: GPSInfo;
  courseStatus?: CourseStatus;
  deviceId?: string;
  deviceName?: string;
}

const formatDate = (timestamp: number) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return format(date, "dd:MM:yyyy");
};

const TripReport: React.FC = () => {
  const { getPrimaryColor, getPrimaryLightColor } = useTheme();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<DeviceOption[]>(
    id ? [{ id, name: id }] : []
  );
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0); // Today 12:00 AM
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [reportFetched, setReportFetched] = useState(false);

  const handleGenerateReport = async () => {
    if (!selectedDevices.length) {
      setError("Please select at least one device.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fromDateStr = moment(fromDate)
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss[Z]");

      const toDateStr = moment(toDate)
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss[Z]");

      // Create device name mapping
      const deviceIdToName: Record<string, string> = Object.fromEntries(
        selectedDevices.map((device) => [device.id, device.name || device.id])
      );

      const selectedDeviceIds = selectedDevices.map((device) => device.id);

      // Fetch all selected devices in parallel
      const responses = await Promise.all(
        selectedDeviceIds.map((deviceId) =>
          axiosInstance
            .get(
              `/gpstracker/device/${deviceId}/report?from=${fromDateStr}&to=${toDateStr}&report=trip`
            )
            .then((response) => ({ response, deviceId }))
        )
      );

      const allResults: ReportData[] = [];
      for (const { response, deviceId } of responses) {
        if (response.data?.status) {
          const transformedData = response.data?.responseData?.map(
            (trip: any) => ({
              startTime: trip.start_time,
              endTime: trip.end_time,
              startOdometer: trip.start_odometer,
              endOdometer: trip.end_odometer,
              distanceKm: trip.distance_km,
              startLat: trip.start_lat,
              startLong: trip.start_long,
              endLat: trip.end_lat,
              endLong: trip.end_long,
              startAddress: trip.start_address,
              endAddress: trip.end_address,
              duration: trip.duration,
              batteryLevel: trip.battery_level,
              gpsInfo: trip.gps_info,
              courseStatus: trip.course_status,
              deviceId,
              deviceName: deviceIdToName[deviceId] || deviceId,
            })
          );
          allResults.push(...(transformedData || []));
        }
      }

      // Sort by start time then by device name for stable display
      const sortedResults = allResults.sort((a, b) => {
        const timeDiff = a.startTime - b.startTime;
        if (timeDiff !== 0) return timeDiff;
        return (a.deviceName || "").localeCompare(b.deviceName || "");
      });

      setReportData(sortedResults);
      setPage(0);
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Error generating report. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
      setReportFetched(true);
    }
  };

  const handleExportExcel = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      // Create header rows like your CSV format
      const headerRows = [
        ["From DateTime", "", moment(fromDate).format('DD:MM:YYYY'), "", "", "", "", "", "", ""],
        ["To DateTime", "", moment(toDate).format('DD:MM:YYYY'), "", "", "", "", "", "", ""],
        ["Vehicle No", "", selectedDevices.map(device => device.name || device.id).join(', '), "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", "", ""], // Empty row
        ["Sr.", "Device", "Start Time", "End Time", "Start Odometer (km)", "End Odometer (km)", "Distance (km)", "Start Location", "End Location", "Start Address", "End Address"]
      ];

      // Create data rows
      const dataRows = reportData.map((trip, index) => [
        index + 1,
        trip?.deviceName || trip?.deviceId || "-",
        formatDate(trip.startTime),
        formatDate(trip.endTime),
        trip.startOdometer.toFixed(2),
        trip.endOdometer.toFixed(2),
        trip.distanceKm.toFixed(2),
        `${trip.startLat}, ${trip.startLong}`,
        `${trip.endLat}, ${trip.endLong}`,
        trip.startAddress,
        trip.endAddress
      ]);

      // Combine header and data rows
      const allRows = [...headerRows, ...dataRows];

      setDownloadProgress(50);

      const worksheet = XLSX.utils.aoa_to_sheet(allRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Trip Report");

      setDownloadProgress(75);

      XLSX.writeFile(
        workbook,
        `Trip_Report_${format(new Date(), "yyyy-MM-dd_HH-mm")}.xlsx`
      );

      setDownloadProgress(100);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      setError("Failed to export report. Please try again.");
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 1000);
    }
  };

  const handleExportPDF = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(14);
      doc.text('Trip Report', 14, 16);
      doc.setFontSize(10);
      doc.text(`From: ${moment(fromDate).format('DD:MM:YYYY')}`, 14, 24);
      doc.text(`To: ${moment(toDate).format('DD:MM:YYYY')}`, 110, 24);
      doc.text(`Devices: ${selectedDevices.map(device => device.name || device.id).join(', ')}`, 14, 30);

      const columns = [
        'Device',
        'Start Time',
        'End Time',
        'Start Odometer (km)',
        'End Odometer (km)',
        'Distance (km)',
        'Start Location',
        'End Location',
        'Start Address',
        'End Address'
      ];

      const rows = reportData.map(trip => [
        trip?.deviceName || trip?.deviceId || '-',
        formatDate(trip.startTime),
        formatDate(trip.endTime),
        trip.startOdometer.toFixed(2),
        trip.endOdometer.toFixed(2),
        trip.distanceKm.toFixed(2),
        `${trip.startLat}, ${trip.startLong}`,
        `${trip.endLat}, ${trip.endLong}`,
        trip.startAddress,
        trip.endAddress
      ]);

      setDownloadProgress(30);

      // Use autotable plugin
      // @ts-ignore
      (autoTable as any)(doc, {
        head: [columns],
        body: rows,
        startY: 36,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [230, 236, 255] },
        theme: 'grid',
        didDrawPage: (dataArg: any) => {
          // page footer
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : (pageSize as any).getHeight();
          doc.setFontSize(9);
          const pageNumber = doc.getNumberOfPages();
          doc.text(`Page ${pageNumber}`, doc.internal.pageSize.getWidth() - 30, pageHeight - 10);
        }
      });

      setDownloadProgress(80);

      doc.save(`Trip_Report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`);

      setDownloadProgress(100);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 800);
    }
  };

  const currentData = reportData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // CSS styles for the component
  const styles = `
    .trip-report-container {
      font-family: "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                      background-color: ${alpha(
                        getPrimaryColor() || "#1D2B56",
                        0.02
                      )};
      min-height: 100vh;
    }

    .header-section {
      background-color: white;
      border-bottom: 1px solid #e2e8f0;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .header-content {
      padding: 1.5rem 1.5rem;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .back-button {
      color: #6b7280;
      transition: color 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 0.5rem;
      transition: all 0.2s ease;
    }

    .back-button:hover {
      color: #374151;
      background-color: #f3f4f6;
    }

    .back-icon {
      width: 1.5rem;
      height: 1.5rem;
    }

    .page-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0;
    }

    .error-alert {
      margin: 1rem 1.5rem;
    }

    .main-content {
      padding: 1.5rem;
    }

    .filter-panel {
      background-color: white;
      border-radius: 0.5rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      border: 1px solid ${alpha(getPrimaryColor() || "#1D2B56", 0.2)};
      padding: 1rem;
      margin-bottom: 1.5rem;
    }

    .filter-grid {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: nowrap;
      justify-content: flex-start;
    }

    .filter-item {
      display: flex;
      flex-direction: column;
      min-width: 160px;
      max-width: 200px;
      flex: 0 0 auto;
    }

    .filter-label {
      display: block;
      font-size: 0.75rem;
      font-weight: 500;
      color: #374151;
      margin-bottom: 0.25rem;
    }

    .date-input {
      width: 100%;
      font-size: 0.75rem;
      transition: all 0.2s ease;
    }

    .date-input:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
    }

    .device-select {
      width: 100%;
      font-size: 0.75rem;
    }

    .device-option {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
    }

    .device-icon {
      color: #6b7280;
      font-size: 1rem;
    }

    .device-name {
      font-size: 0.75rem;
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      flex-shrink: 0;
      margin-left: auto;
    }

    .action-buttons {
      display: flex;
      gap: 0.375rem;
    }

    .generate-btn {
      background-color: var(--primary-color) !important;
      color: white !important;
      padding: 0.4rem 0.8rem !important;
      border-radius: 0.25rem !important;
      font-weight: 500 !important;
      text-transform: none !important;
      font-size: 0.7rem !important;
      transition: all 0.2s ease !important;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
      min-width: 75px;
      height: 32px;
    }

    .generate-btn:hover {
      background-color: var(--primary-light) !important;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
    }

    .export-btn {
      padding: 0.4rem 0.8rem !important;
      border-radius: 0.25rem !important;
      font-weight: 500 !important;
      text-transform: none !important;
      font-size: 0.7rem !important;
      transition: all 0.2s ease !important;
      min-width: 65px;
      height: 32px;
      border-width: 1px !important;
    }

    .excel-btn {
      color: #059669 !important;
      border-color: #059669 !important;
    }

    .excel-btn:hover {
      background-color: #059669 !important;
      color: white !important;
    }

    .pdf-btn {
      color: #dc2626 !important;
      border-color: #dc2626 !important;
    }

    .pdf-btn:hover {
      background-color: #dc2626 !important;
      color: white !important;
    }

    .table-container {
      background-color: white;
      border-radius: 0.75rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
      overflow: hidden;
    }

    .table-wrapper {
      overflow-x: auto;
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .table-header {
      background: linear-gradient(to right, #eff6ff, #e0e7ff);
      border-bottom: 1px solid #e5e7eb;
    }

    .table-header-cell {
      padding: 1rem 1.5rem;
      text-align: left;
      font-size: 0.75rem;
      font-weight: 600;
      color: #4b5563;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .table-body {
      background-color: white;
    }

    .table-row {
      border-bottom: 1px solid #f3f4f6;
      transition: all 0.15s ease;
    }

    .table-row:hover {
      background-color: #eff6ff;
    }

    .row-even {
      background-color: white;
    }

    .row-odd {
      background-color: #f9fafb;
    }

    .table-cell {
      padding: 1rem 1.5rem;
      white-space: nowrap;
    }

    .time-cell {
      font-size: 0.875rem;
      color: #1f2937;
    }

    .odometer-cell {
      font-size: 0.875rem;
      font-weight: 500;
      color: #1f2937;
    }

    .distance-cell {
      padding: 1rem 1.5rem;
      white-space: nowrap;
    }

    .distance-chip {
      display: inline-flex;
      align-items: center;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 600;
      background-color: #dbeafe;
      color: #1e40af;
      border: 1px solid #bfdbfe;
    }

    .location-cell {
      font-size: 0.875rem;
      color: #1f2937;
      font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
    }

    .address-cell {
      font-size: 0.875rem;
      color: #1f2937;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 200px;
    }

    .battery-cell {
      font-size: 0.875rem;
      color: #1f2937;
    }

    .gps-chip {
      display: inline-flex;
      align-items: center;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 600;
      background-color: #e0f2fe;
      color: #0c4a6e;
      border: 1px solid #bae6fd;
    }

    .course-cell {
      font-size: 0.875rem;
      color: #1f2937;
    }

    .pagination-container {
      background-color: #f9fafb;
      padding: 1rem 1.5rem;
      border-top: 1px solid #e5e7eb;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .pagination-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .rows-text {
      font-size: 0.875rem;
      color: #374151;
    }

    .rows-select {
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      padding: 0.25rem 0.75rem;
      font-size: 0.875rem;
      background-color: white;
      color: #374151;
      transition: all 0.2s ease;
    }

    .rows-select:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .pagination-right {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .pagination-info {
      font-size: 0.875rem;
      color: #374151;
    }

    .pagination-buttons {
      display: flex;
      gap: 0.25rem;
    }

    .pagination-btn {
      padding: 0.5rem 0.75rem;
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      font-size: 0.875rem;
      background-color: white;
      color: #374151;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .pagination-btn:hover:not(:disabled) {
      background-color: white;
      border-color: #9ca3af;
    }

    .pagination-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .no-data-container {
      background-color: white;
      border-radius: 0.75rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
      padding: 3rem;
      text-align: center;
    }

    .no-data-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    .no-data-text {
      font-size: 1rem;
      color: #6b7280;
    }

    @media (max-width: 1200px) {
      .filter-item {
        min-width: 140px;
        max-width: 160px;
      }
      
      .generate-btn,
      .export-btn {
        min-width: 60px !important;
        font-size: 0.65rem !important;
        padding: 0.3rem 0.6rem !important;
      }
    }

    @media (max-width: 992px) {
      .filter-panel {
        padding: 0.75rem;
      }

      .filter-grid {
        gap: 0.375rem;
      }

      .filter-item {
        min-width: 120px;
        max-width: 140px;
      }

      .generate-btn,
      .export-btn {
        min-width: 55px !important;
        font-size: 0.6rem !important;
        padding: 0.25rem 0.5rem !important;
        height: 28px;
      }
    }

    @media (max-width: 768px) {
      .filter-panel {
        padding: 0.5rem;
      }

      .filter-grid {
        flex-direction: column;
        align-items: stretch;
        gap: 0.5rem;
      }

      .filter-item {
        min-width: auto;
        max-width: none;
      }

      .filter-actions {
        margin-top: 0.5rem;
        margin-left: 0;
        justify-content: center;
      }

      .action-buttons {
        justify-content: center;
        gap: 0.25rem;
      }

      .generate-btn,
      .export-btn {
        min-width: 60px !important;
        font-size: 0.65rem !important;
        padding: 0.3rem 0.6rem !important;
        height: 30px;
      }

      .pagination-container {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
      }

      .pagination-right {
        justify-content: space-between;
      }
    }

    @media (max-width: 640px) {
      .main-content {
        padding: 1rem;
      }

      .filter-panel {
        padding: 1rem;
      }

      .table-header-cell,
      .table-cell {
        padding: 0.75rem;
      }

      .no-data-container {
        padding: 2rem;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="trip-report-container">
        {/* Header */}
        <div className="header-section">
          <div className="header-content">
            <div className="header-left">
              <Link to="/reports" className="back-button">
                <ArrowBackIcon className="back-icon" />
              </Link>
              <h1 className="page-title">Trip Report</h1>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-alert">
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        <div className="main-content">
          {/* Filter Panel */}
          <div className="filter-panel">
            <div className="filter-grid">
              <div className="filter-item">
                <label className="filter-label">From Date</label>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    value={fromDate}
                    onChange={(newValue: Date | null) =>
                      newValue && setFromDate(newValue)
                    }
                    maxDateTime={toDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        className: "date-input",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div className="filter-item">
                <label className="filter-label">To Date</label>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    value={toDate}
                    onChange={(newValue: Date | null) =>
                      newValue && setToDate(newValue)
                    }
                    minDateTime={fromDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        className: "date-input",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              <div
                className="filter-item"
                style={{ minWidth: "200px", maxWidth: "300px" }}
              >
                <label className="filter-label">Select Devices</label>
                <DeviceSearchSelect
                  label="Add Device"
                  multiple
                  value={selectedDevices}
                  onChange={(newValue) =>
                    setSelectedDevices((newValue as DeviceOption[]) || [])
                  }
                  maxSelection={10}
                  onMaxSelectionReached={() =>
                    setError("Maximum 10 devices can be selected.")
                  }
                />
                {/* Selected Devices Summary */}
                {selectedDevices.length > 0 && (
                  <div style={{ marginTop: "0.5rem" }}>
                    {selectedDevices.length > 50 ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: "#e3f2fd",
                          border: "1px solid #90caf9",
                          borderRadius: "8px",
                          padding: "0.5rem 0.75rem",
                          gap: "0.5rem",
                        }}
                      >
                        <span
                          style={{
                            color: "#0284F5",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                          }}
                        >
                          {`${selectedDevices.length.toLocaleString()} Devices Selected`}
                        </span>
                        <button
                          onClick={() => setSelectedDevices([])}
                          style={{
                            background: "none",
                            border: "1px solid #0284F5",
                            color: "#0284F5",
                            cursor: "pointer",
                            padding: "0.25rem 0.5rem",
                            fontSize: "0.7rem",
                            borderRadius: "4px",
                          }}
                        >
                          Clear All
                        </button>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "0.5rem",
                        }}
                      >
                          {selectedDevices.map((device) => {
                          return (
                            <div
                                key={device.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#e3f2fd",
                                border: "1px solid #90caf9",
                                borderRadius: "16px",
                                padding: "0.25rem 0.5rem",
                                gap: "0.25rem",
                              }}
                            >
                              <span
                                style={{ color: "#0284F5", fontSize: "0.75rem" }}
                              >
                                {device.name || device.id}
                              </span>
                              <button
                                onClick={() =>
                                  setSelectedDevices((prev) =>
                                    prev.filter((selected) => selected.id !== device.id)
                                  )
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#0284F5",
                                  cursor: "pointer",
                                  padding: "0",
                                  fontSize: "0.75rem",
                                  fontWeight: "bold",
                                }}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="filter-actions">
                <div className="action-buttons">
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleGenerateReport}
                    disabled={loading || !selectedDevices.length}
                    className="generate-btn"
                    sx={{
                      bgcolor: getPrimaryColor() || "#1D2B56",
                      "&:hover": {
                        bgcolor: getPrimaryLightColor(),
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      "Generate"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportExcel}
                    disabled={downloading || reportData.length === 0}
                    className="export-btn excel-btn"
                  >
                    {downloading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      "Excel"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PdfIcon />}
                    onClick={handleExportPDF}
                    disabled={reportData.length === 0}
                    className="export-btn pdf-btn"
                  >
                    PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          {reportData.length > 0 ? (
            <div className="table-container">
              <div className="table-wrapper">
                <table className="data-table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Device</th>
                      <th className="table-header-cell">Start Time</th>
                      <th className="table-header-cell">End Time</th>
                      <th className="table-header-cell">Start Odometer (km)</th>
                      <th className="table-header-cell">End Odometer (km)</th>
                      <th className="table-header-cell">Distance (km)</th>
                      <th className="table-header-cell">Start Location</th>
                      <th className="table-header-cell">End Location</th>
                      <th className="table-header-cell">Start Address</th>
                      <th className="table-header-cell">End Address</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {currentData.map((trip, index) => (
                      <tr
                        key={index}
                        className={`table-row ${
                          index % 2 === 0 ? "row-even" : "row-odd"
                        }`}
                      >
                        <td
                          className="table-cell"
                          style={{ fontWeight: 600, color: "#1f2937" }}
                        >
                          {trip?.deviceName || trip?.deviceId || "-"}
                        </td>
                        <td className="table-cell time-cell">
                          {formatDate(trip.startTime)}
                        </td>
                        <td className="table-cell time-cell">
                          {formatDate(trip.endTime)}
                        </td>
                        <td className="table-cell odometer-cell">
                          {trip.startOdometer.toFixed(2)}
                        </td>
                        <td className="table-cell odometer-cell">
                          {trip.endOdometer.toFixed(2)}
                        </td>
                        <td className="distance-cell">
                          <span className="distance-chip">
                            <RouteIcon
                              style={{
                                fontSize: "0.875rem",
                                marginRight: "0.25rem",
                              }}
                            />
                            {trip.distanceKm.toFixed(2)} km
                          </span>
                        </td>
                        <td className="table-cell location-cell">{`${trip.startLat}, ${trip.startLong}`}</td>
                        <td className="table-cell location-cell">{`${trip.endLat}, ${trip.endLong}`}</td>
                        <td className="table-cell address-cell">
                          {trip.startAddress}
                        </td>
                        <td className="table-cell address-cell">
                          {trip.endAddress}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination-container">
                <div className="pagination-left">
                  <span className="rows-text">Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    className="rows-select"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="pagination-right">
                  <span className="pagination-info">
                    {page * rowsPerPage + 1}-
                    {Math.min((page + 1) * rowsPerPage, reportData.length)} of{" "}
                    {reportData.length}
                  </span>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                      className="pagination-btn"
                    >
                      ←
                    </button>
                    <button
                      onClick={() =>
                        setPage(
                          Math.min(
                            Math.ceil(reportData.length / rowsPerPage) - 1,
                            page + 1
                          )
                        )
                      }
                      disabled={
                        page >= Math.ceil(reportData.length / rowsPerPage) - 1
                      }
                      className="pagination-btn"
                    >
                      →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            reportFetched && (
              <div className="no-data-container">
                <div className="no-data-content">
                  <InfoIcon color="action" />
                  <span className="no-data-text">No data found</span>
                </div>
              </div>
            )
          )}
        </div>

        {/* Download Progress Dialog */}
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={downloading}
        >
          <Box
            sx={{
              bgcolor: "background.paper",
              p: 4,
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            <CircularProgress
              variant="determinate"
              value={downloadProgress}
              size={60}
              thickness={4}
              sx={{ mb: 2 }}
            />
            <Typography variant="h6" color="text.primary">
              {downloadProgress < 100
                ? "Preparing Download..."
                : "Download Complete!"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {downloadProgress}% Complete
            </Typography>
          </Box>
        </Backdrop>
      </div>
    </>
  );
};

export default TripReport;
